import type { BrandListItem } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { isCustomerRole } from "@nlc/shared-types";
import { Link } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import { Avatar } from "@/components/avatar";
import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { useTableSorting } from "@/hooks/useTableSorting";
import { useUser } from "@/hooks/useUser";
import { dateFormatter } from "@/utils/dateFormatter";

import { COLUMN_WIDTHS } from "../shared/columnWidths";

interface BrandsTableProps {
  brands: BrandListItem[];
  isLoading?: boolean;
  onSaveBrandOrder?: (reorderedBrands: BrandListItem[]) => Promise<void>;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

const BrandCell = memo(function BrandCell({
  brandId,
  name,
  profilePic,
}: {
  brandId: string;
  name: string;
  profilePic?: string;
}) {
  const displayName = name || "Untitled";

  return (
    <Link
      className="hover:no-underline"
      params={{ brandId }}
      to="/brands/$brandId/overview"
    >
      <div className="flex items-center py-3 pr-3 min-w-0 w-full overflow-hidden">
        <Avatar
          alt={displayName}
          className="size-10 rounded-full border border-border-secondary shrink-0"
          objectFit="contain"
          src={profilePic}
        />
        <p className="ml-3 text-sm font-medium text-text-primary leading-tight min-w-0 line-clamp-2 break-words">
          {displayName}
        </p>
      </div>
    </Link>
  );
});

const NameCell = memo(function NameCell({
  row,
}: {
  row: { original: BrandListItem };
}) {
  return (
    <BrandCell
      brandId={row.original.id}
      name={row.original.name}
      profilePic={row.original.profilePicture || undefined}
    />
  );
});

const IndustryCell = memo(function IndustryCell({ value }: { value: unknown }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <div className="text-text-secondary font-bold text-sm leading-tight line-clamp-2 break-words">
        {(value as string) || "-"}
      </div>
    </div>
  );
});

const WebAddressCell = memo(function WebAddressCell({
  value,
}: {
  value: unknown;
}) {
  const webAddress = value as string;

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      {webAddress ? (
        <a
          className="text-secondary hover:underline text-text-tertiary text-sm leading-tight block min-w-0 w-full overflow-hidden"
          href={webAddress}
          rel="noopener noreferrer"
          style={{
            display: "-webkit-box",
            maxHeight: "calc(2 * 1.25 * 0.875rem)",
            overflow: "hidden",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            wordBreak: "break-all",
          }}
          target="_blank"
          title={webAddress}
        >
          {webAddress}
        </a>
      ) : (
        <span className="text-text-tertiary text-sm">-</span>
      )}
    </div>
  );
});

const AccountCell = memo(function AccountCell({ value }: { value: unknown }) {
  const account = value as BrandListItem["account"];

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      {account ? (
        <Link
          className="text-text-secondary text-sm leading-tight line-clamp-2 break-words hover:underline"
          params={{ projectId: String(account.id) }}
          to="/projects/$projectId"
        >
          {account.name}
        </Link>
      ) : (
        <span className="text-text-secondary text-sm">-</span>
      )}
    </div>
  );
});

/*
// Uncomment when createdBy is ready to be implemented
const CreatedByCell = memo(function CreatedByCell({
  value,
}: {
  value: unknown;
}) {
  const createdBy = value as BrandListItem["createdBy"];

  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      {createdBy ? (
        <div
          className="text-text-tertiary text-sm leading-tight line-clamp-2 break-words"
          title={createdBy.name}
        >
          {createdBy.name}
        </div>
      ) : (
        <span className="text-text-tertiary text-sm">-</span>
      )}
    </div>
  );
}); */

const CreatedAtCell = memo(function CreatedAtCell({
  value,
}: {
  value: unknown;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden">
      <span className="text-text-tertiary text-sm">
        {dateFormatter(value as string)}
      </span>
    </div>
  );
});

export const BrandsTable = memo(function BrandsTable({
  brands,
  isLoading = false,
  pagination,
  setSortDescriptor,
  sortDescriptor,
}: BrandsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const { data: userData } = useUser();
  const isTeamMember = !isCustomerRole(userData?.user?.roleId || 0);

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current.querySelector("table");

      if (table) {
        table.style.tableLayout = "fixed";
        table.style.width = "100%";
      }

      const cells = tableRef.current.querySelectorAll("td");

      for (const cell of cells) {
        cell.style.overflow = "hidden";
        cell.style.maxWidth = "0";
      }
    }
  }, [brands]);

  const columns: ColumnDef<BrandListItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ row }) => <NameCell row={row} />,
        enableSorting: true,
        header: "Name",
        maxSize: COLUMN_WIDTHS.NAME,
        minSize: COLUMN_WIDTHS.NAME,
        size: COLUMN_WIDTHS.NAME,
      },
      ...(isTeamMember
        ? [
            {
              accessorKey: "account",
              cell: ({ getValue }: { getValue: () => unknown }) => (
                <AccountCell value={getValue()} />
              ),
              enableMultiRemove: false,
              enableSorting: false,
              header: "Account",
              maxSize: COLUMN_WIDTHS.ACCOUNT,
              minSize: COLUMN_WIDTHS.ACCOUNT,
              size: COLUMN_WIDTHS.ACCOUNT,
            },
          ]
        : []),
      {
        accessorKey: "industry",
        cell: ({ getValue }) => <IndustryCell value={getValue()} />,
        enableMultiRemove: false,
        enableSorting: true,
        header: "Industry",
        maxSize: COLUMN_WIDTHS.INDUSTRY,
        minSize: COLUMN_WIDTHS.INDUSTRY,
        size: COLUMN_WIDTHS.INDUSTRY,
      },
      {
        accessorKey: "webAddress",
        cell: ({ getValue }) => <WebAddressCell value={getValue()} />,
        enableMultiRemove: false,
        enableSorting: true,
        header: "Web address",
        maxSize: COLUMN_WIDTHS.WEB_ADDRESS,
        minSize: COLUMN_WIDTHS.WEB_ADDRESS,
        size: COLUMN_WIDTHS.WEB_ADDRESS,
      },
      /*  
      * We are not showing the createdBy column for now until 
      * create brand is implemented in this repo
     {
        accessorKey: "createdBy",
        cell: ({ getValue }) => <CreatedByCell value={getValue()} />,
        enableMultiRemove: false,
        enableSorting: true,
        header: "Created by",
        maxSize: COLUMN_WIDTHS.CREATED_BY,
        minSize: COLUMN_WIDTHS.CREATED_BY,
        size: COLUMN_WIDTHS.CREATED_BY,
      }, */
      {
        accessorKey: "createdAt",
        cell: ({ getValue }) => <CreatedAtCell value={getValue()} />,
        enableMultiRemove: false,
        enableSorting: true,
        header: "Created on",
        maxSize: COLUMN_WIDTHS.CREATED_DATE,
        minSize: COLUMN_WIDTHS.CREATED_DATE,
        size: COLUMN_WIDTHS.CREATED_DATE,
      },
    ],
    [isTeamMember],
  );

  const getRowId = useCallback((brand: BrandListItem) => brand.id, []);

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = useMemo(
    () => ["name", "industry", "webAddress", "createdBy", "createdAt"],
    [],
  );

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <Table
        className="rounded-b-xl brands-table w-full"
        columns={columns}
        data={brands}
        getRowId={getRowId}
        isLoading={isLoading}
        isServerSideSorting
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
